import { DateTime } from 'luxon';
import { DateUtils } from '@onrsr/ui/core';

export type OccurrenceCategory = 'A' | 'B';
export type Severity = 'Major' | 'Significant' | 'Minor';
export type RegulatoryValue = 'Marginal' | 'Moderate' | 'Substantial';
export type Priority = 'Very High' | 'High' | 'Medium' | 'Low';
export type IrgStatus = 'Open' | 'Closed';

export const IRG_STATUS_OPTIONS: IrgStatus[] = ['Open', 'Closed'];

export interface Organisation {
  id: number;
  name: string;
  abbreviation: string;
}

export interface OccurrenceEventType {
  label: string;
}

export interface OccurrenceStatus {
  label: string;
}

/** Per-office triage and IRG review data for a single occurrence. */
export interface OfficeIrgReview {
  office: string;
  rsoReview: boolean;
  rsoReviewedBy: string | null;
  rsoReviewDateTime: string | null;
  rsoReviewNotes: string | null;
  escalateToIrg: boolean;
  escalateToIrgBy: string | null;
  escalateToIrgDateTime: string | null;
  escalateToIrgComments: string | null;
  severity: Severity | null;
  regulatoryValue: RegulatoryValue | null;
  priority: Priority | null;
  prioritisedFollowUp: string | null;
  seriousOccurrence: boolean;
  irgComments: string | null;
  leadRso: string | null;
  irgStatus: IrgStatus | null;
  relatedActivities: RelatedActivity[];
}

export interface OccurrenceReview {
  id: number;
  amalgamatedId: number;
  /** Category A occurrences are automatically flagged for IRG escalation. */
  escalatedToIrg: boolean;
  operatorReference: string;
  occurrenceDateTime: string;
  initialNotificationDateTime: string;
  finalSubmissionDateTime: string | null;
  finalReportDue: string;
  status: OccurrenceStatus;
  category: OccurrenceCategory;
  state: string;
  notifyingOrganisation: Organisation;
  rimOrganisation: Organisation;
  rsoOrganisation: Organisation;
  locationDescription: string;
  eventTypes: OccurrenceEventType[];
  description: string;
  irgReview: OfficeIrgReview[];
}

/** Flat review fields derived from a single office slice — used by grids, panels, and dialogs. */
export interface OccurrenceOfficeReviewFields {
  rsoReview: boolean;
  rsoReviewedBy: string | null;
  rsoReviewOffice: string | null;
  rsoReviewDateTime: string | null;
  rsoReviewNotes: string | null;
  escalateToIrg: boolean;
  escalateToIrgBy: string | null;
  escalateToIrgOffice: string | null;
  escalateToIrgDateTime: string | null;
  escalateToIrgComments: string | null;
  severity: Severity | null;
  regulatoryValue: RegulatoryValue | null;
  priority: Priority | null;
  prioritisedFollowUp: string | null;
  relatedActivities: RelatedActivity[];
  seriousOccurrence: boolean;
  irgComments: string | null;
  leadRso: string | null;
  irgStatus: IrgStatus | null;
}

export type OccurrenceReviewOfficeView = OccurrenceReview & OccurrenceOfficeReviewFields;

export interface RelatedActivity {
  id: number;
  reference: string;
  activityType: string;
  status: string;
  startDate: string;
  organisation: string;
  office: string;
}

export const STATE_OFFICE: Record<string, string> = {
  SA: 'Adelaide',
  NSW: 'Sydney',
  VIC: 'Melbourne',
  QLD: 'Brisbane',
  WA: 'Perth',
  TAS: 'Hobart',
};

export function defaultOfficeReview(office: string): OfficeIrgReview {
  return {
    office,
    rsoReview: false,
    rsoReviewedBy: null,
    rsoReviewDateTime: null,
    rsoReviewNotes: null,
    escalateToIrg: false,
    escalateToIrgBy: null,
    escalateToIrgDateTime: null,
    escalateToIrgComments: null,
    severity: null,
    regulatoryValue: null,
    priority: null,
    prioritisedFollowUp: null,
    seriousOccurrence: false,
    irgComments: null,
    leadRso: null,
    irgStatus: null,
    relatedActivities: [],
  };
}

export function findOfficeReview(
  occurrence: Pick<OccurrenceReview, 'irgReview'>,
  office: string,
): OfficeIrgReview | undefined {
  return occurrence.irgReview?.find((review) => review.office === office);
}

export function getOfficeReview(occurrence: Pick<OccurrenceReview, 'irgReview'>, office: string): OfficeIrgReview {
  return findOfficeReview(occurrence, office) ?? defaultOfficeReview(office);
}

export function officeReviewToFlatFields(review: OfficeIrgReview): OccurrenceOfficeReviewFields {
  return {
    rsoReview: review.rsoReview,
    rsoReviewedBy: review.rsoReviewedBy,
    rsoReviewOffice: review.office,
    rsoReviewDateTime: review.rsoReviewDateTime,
    rsoReviewNotes: review.rsoReviewNotes,
    escalateToIrg: review.escalateToIrg,
    escalateToIrgBy: review.escalateToIrgBy,
    escalateToIrgOffice: review.office,
    escalateToIrgDateTime: review.escalateToIrgDateTime,
    escalateToIrgComments: review.escalateToIrgComments,
    severity: review.severity,
    regulatoryValue: review.regulatoryValue,
    priority: review.priority,
    prioritisedFollowUp: review.prioritisedFollowUp,
    relatedActivities: review.relatedActivities,
    seriousOccurrence: review.seriousOccurrence,
    irgComments: review.irgComments,
    leadRso: review.leadRso,
    irgStatus: review.irgStatus,
  };
}

export function occurrenceForOffice(occurrence: OccurrenceReview, office: string): OccurrenceReviewOfficeView {
  return {
    ...occurrence,
    ...officeReviewToFlatFields(getOfficeReview(occurrence, office)),
  };
}

export function updateOfficeReview(
  occurrence: OccurrenceReview,
  office: string,
  patch: Partial<Omit<OfficeIrgReview, 'office'>>,
): OccurrenceReview {
  const existing = findOfficeReview(occurrence, office);
  const nextReview: OfficeIrgReview = {
    ...(existing ?? defaultOfficeReview(office)),
    ...patch,
    office,
  };

  const irgReview = existing
    ? occurrence.irgReview.map((review) => (review.office === office ? nextReview : review))
    : [...occurrence.irgReview, nextReview];

  return { ...occurrence, irgReview };
}

export function isEscalatedForOffice(
  occurrence: Pick<OccurrenceReview, 'escalatedToIrg' | 'category' | 'irgReview'>,
  office: string,
): boolean {
  if (occurrence.escalatedToIrg || occurrence.category === 'A') {
    return true;
  }
  const review = findOfficeReview(occurrence, office);
  return review?.escalateToIrg === true;
}

export function isEscalatedToIrg(
  occurrence: Pick<OccurrenceReview, 'escalatedToIrg' | 'category' | 'irgReview'>,
): boolean {
  if (occurrence.escalatedToIrg || occurrence.category === 'A') {
    return true;
  }
  return occurrence.irgReview.some((review) => review.escalateToIrg);
}

export function officesWithReviewActivity(occurrence: OccurrenceReview): OfficeIrgReview[] {
  return occurrence.irgReview.filter(
    (review) =>
      review.rsoReview ||
      review.escalateToIrg ||
      review.severity != null ||
      review.priority != null ||
      review.irgComments != null ||
      review.leadRso != null,
  );
}

export function resolveSeedOffice(
  seed: {
    state: string;
    rsoReviewOffice?: string | null;
    escalateToIrgOffice?: string | null;
  },
  explicitOffice?: string,
): string {
  return explicitOffice ?? seed.rsoReviewOffice ?? seed.escalateToIrgOffice ?? STATE_OFFICE[seed.state] ?? 'Adelaide';
}

/** Resolves the RSO office for a related activity using the same office list as IRG escalation. */
export function relatedActivityOffice(
  occurrence: Pick<OccurrenceReview, 'state' | 'irgReview'>,
  office?: string,
): string {
  const targetOffice = office ?? STATE_OFFICE[occurrence.state] ?? 'Adelaide';
  const review = findOfficeReview(occurrence, targetOffice);
  return review?.office ?? targetOffice;
}

export function withRelatedActivityDefaults(
  occurrence: Pick<OccurrenceReview, 'notifyingOrganisation' | 'state' | 'irgReview'>,
  activities: Omit<RelatedActivity, 'organisation' | 'office'>[],
  office: string,
): RelatedActivity[] {
  const organisation = occurrence.notifyingOrganisation.name;
  const activityOffice = relatedActivityOffice(occurrence, office);
  return activities.map((activity) => ({
    ...activity,
    organisation,
    office: activityOffice,
  }));
}

export interface PrioritisationSelection {
  severity: Severity;
  regulatoryValue: RegulatoryValue;
}

export function formatDateTime(value: string | null): string {
  return value ? DateUtils.formatDate(value) : '';
}

export function formatDate(value: string | null): string {
  return value ? DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED) : '';
}

export function getStatusClass(status: string): string {
  switch (status) {
    case 'Final':
      return 'status-final';
    case 'Submitted':
      return 'status-submitted';
    case 'Awaiting Final Submission':
      return 'status-awaiting-final-submission';
    case 'Awaiting Further Information':
      return 'status-awaiting-further-information';
    case 'Closed':
      return 'status-closed';
    default:
      return 'status-default';
  }
}
